import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Instagram, Send } from 'lucide-react';
import { photographerAPI, contactAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    dsgvo_consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photographerInfo, setPhotographerInfo] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPhotographerInfo();
  }, []);

  const fetchPhotographerInfo = async () => {
    try {
      const response = await photographerAPI.getInfo();
      setPhotographerInfo(response.info);
    } catch (error) {
      console.error('Error fetching photographer info:', error);
      // Use default values
      setPhotographerInfo({
        email: 'kontakt@franziska-photography.de',
        phone: '+49 123 456789',
        location: 'Deutschland',
        socialMedia: {
          instagram: 'https://www.instagram.com/memories_moments_photos?igsh=MWpic2ZwbGkzeXNodQ==',
          facebook: 'https://facebook.com/franziskaphotography',
          pinterest: 'https://pinterest.com/franziskaphotos'
        }
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.dsgvo_consent) {
      toast({
        title: "Zustimmung erforderlich",
        description: "Bitte stimmen Sie der Datenschutzerklärung zu, um Ihre Nachricht zu senden.",
        variant: "destructive"
      });
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await contactAPI.submit(formData);
      toast({
        title: "Nachricht gesendet!",
        description: response.message || "Vielen Dank für Ihre Anfrage. Ich melde mich bald bei Ihnen.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        dsgvo_consent: false
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!photographerInfo) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container text-center">
          <h1 className="hero-title mb-6">Kontakt</h1>
          <p className="subtitle max-w-2xl mx-auto">
            Haben Sie Fragen oder möchten Sie ein Fotoshooting buchen? Ich freue mich auf Ihre Nachricht!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing">
        <div className="photo-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="fade-in-up">
              <h2 className="font-heading text-3xl font-semibold mb-6">Senden Sie mir eine Nachricht</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                    placeholder="+49 123 456789"
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                  >
                    <option value="">Bitte waehlen...</option>
                    <option value="hunde">Hunde-Fotografie</option>
                    <option value="portraits">Portrait-Fotografie</option>
                    <option value="andere">Andere</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                    placeholder="Erzählen Sie mir mehr über Ihr Projekt..."
                  ></textarea>
                </div>
                <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <input
                    type="checkbox"
                    id="dsgvo_consent"
                    name="dsgvo_consent"
                    checked={formData.dsgvo_consent}
                    onChange={handleChange}
                    data-testid="contact-dsgvo-consent"
                    className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="dsgvo_consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Datenschutzerklärung</a> gelesen und stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage elektronisch erhoben und gespeichert werden. Meine Einwilligung kann ich jederzeit für die Zukunft per E-Mail widerrufen. *
                  </label>
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                  <Send size={18} />
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="font-heading text-3xl font-semibold mb-6">Kontaktinformationen</h2>
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">E-Mail</h3>
                    <a href={`mailto:${photographerInfo.email}`} className="text-gray-600 hover:text-primary transition-colors">
                      {photographerInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Telefon</h3>
                    <a href={`tel:${photographerInfo.phone}`} className="text-gray-600 hover:text-primary transition-colors">
                      {photographerInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Standort</h3>
                    <p className="text-gray-600">{photographerInfo.location}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <h3 className="font-heading text-xl font-semibold mb-4">Folgen Sie mir</h3>
                <div className="flex gap-4">
                  <a 
                    href={photographerInfo.socialMedia.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center border-2 border-gray-300 rounded-full hover:border-primary hover:bg-primary hover:text-white transition-all"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </div>

              <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                <h3 className="font-heading text-lg font-semibold mb-3">Verfuegbarkeit</h3>
                <div className="body-text text-gray-600 space-y-4">
                  <p>
                    Da ich einen Hauptjob ausuebe, bin ich meistens am spaeten Nachmittag oder am Wochenende erreichbar.
                  </p>
                  <p>
                    Wenn Sie also Lust auf ein gemeinsames Projekt haben, schreiben Sie mir gerne eine E-Mail oder melden Sie sich gerne per WhatsApp unter {photographerInfo.phone}. Ich melde mich schnellstmoeglich zurueck.
                  </p>
                  <p>
                    Ich freue mich auf eine Nachricht.
                  </p>
                  <p className="font-medium">
                    Liebe Gruesse, Franziska
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
